import { useState, useEffect, useRef } from 'react'
import { compress } from 'compress-json'
import tinycolor from 'tinycolor2'
import * as Helpers from '#/helpers'

export const appendQueryParams = (url, params) => {
  const [baseUrl, fragment] = url.split('#')
  const queryStart = baseUrl.includes('?') ? '&' : '?'
  let query = ''
  Object.entries(params).forEach(([key, value], index) => {
    query += `${index === 0 ? queryStart : '&'}${encodeURIComponent(
      key
    )}=${encodeURIComponent(value)}`
  })

  return `${baseUrl}${query}${fragment ? `#${fragment}` : ''}`
}

export const appUsesURL = (appKey, apps) =>
  !!(
    appKey !== 'IMAGE' &&
    appKey !== 'JSON' &&
    (apps[appKey]?.domain?.length || apps[appKey]?.parent)
  )

export const b64toBlob = (dataURI, contentType = '') => {
  const binary = atob(dataURI.split(',')[1])
  const array = []
  for (let i = 0; i < binary.length; i += 1) {
    array.push(binary.charCodeAt(i))
  }
  return new Blob([new Uint8Array(array)], { type: contentType })
}

export const b64toFile = (dataURI, filename, mimeType) =>
  fetch(dataURI)
    .then((res) => res.arrayBuffer())
    .then((buf) => new File([buf], filename, { type: mimeType }))

export const capitalize = (str) => {
  if (typeof str !== 'string') return ''
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

export const chunkToLimit = (arr, kbLimit) => {
  let chunk = []
  const result = []
  for (let i = 0; i < arr.length; i += 1) {
    const item = compress(arr[i])
    const newSize = new Blob([JSON.stringify(chunk.concat(item))], {
      type: 'application/json',
    }).size
    if (newSize > kbLimit * 1024) {
      result.push(chunk)
      chunk = []
    }
    chunk.push(item)
  }
  if (chunk.length > 0) {
    result.push(chunk)
  }
  return result
}

export const isDark = (hexcolor) => {
  hexcolor = hexcolor.substr(1)
  const r = parseInt(hexcolor.substr(0, 2), 16)
  const g = parseInt(hexcolor.substr(2, 2), 16)
  const b = parseInt(hexcolor.substr(4, 2), 16)
  const yiq = (r * 299 + g * 587 + b * 114) / 1000
  return !(yiq >= 132.2)
}

export const containsUrl = (str = '') => {
  if (typeof str !== 'string') return false
  const urlPattern =
    /(?:https?:\/\/)?(?:www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\/\S*)?/
  return urlPattern.test(str)
}

export const csvToArray = (str, delimiter = ',') => {
  const headers = str.slice(0, str.indexOf('\n')).split(delimiter)
  const rows = str.slice(str.indexOf('\n') + 1).split('\n')
  const arr = rows.map((row) => {
    const values = row.split(delimiter)
    const el = headers.reduce((object, header, index) => {
      object[header] = values[index]
      return object
    }, {})
    return el
  })

  return arr
}

export const encodeString = (str) =>
  str.toLowerCase().replace(/[^a-z0-9._-]+/gi, '-')

export const fetchGeocode = (geocoder, data) => {
  const geocodeAddress = (arr, id, address) =>
    new Promise((resolve) =>
      geocoder.geocode({ address }, async (results, status) => {
        if (status === 'OK' && results.length > 0) {
          const lat = await results[0].geometry.location.lat()
          const lng = await results[0].geometry.location.lng()
          const latlng = `${lat},${lng}`
          AP.request({
            url: `/rest/api/3/issue/${id}/properties/appsplusGeocode`,
            type: 'PUT',
            contentType: 'application/json',
            data: JSON.stringify([
              ...arr?.filter(
                (x) =>
                  x.address?.toLowerCase().trim() !==
                  address?.toLowerCase().trim()
              ),
              { address, latlng },
            ]),
          }).catch((err) => console.error('Error fetching geocode:', err))
          resolve(latlng)
        }
        resolve(null)
      })
    )

  return new Promise((resolve) => {
    if (!data?.address || !data.address.includes(' ')) return resolve(null)
    const trimmedAddress = data?.address?.toLowerCase().trim()

    AP.request(`/rest/api/3/issue/${data.id}/properties/appsplusGeocode`)
      .then((res) => JSON.parse(res.body))
      .then(async (x) => {
        const match = x?.value?.find((y) => trimmedAddress === y.address)
        if (match) {
          resolve(match.latlng)
        } else {
          const latlng = await geocodeAddress(
            x?.value || [],
            data.id,
            trimmedAddress
          )
          resolve(latlng)
        }
      })
      .catch(async () => {
        const latlng = await geocodeAddress([], data.id, trimmedAddress)
        resolve(latlng)
      })
  })
}

export const getTheme = (theme, isJira) =>
  Helpers.themes(isJira).filter((x) => x.theme === theme)?.[0]

export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 255, g: 255, b: 255 }
}

export const jiraPrefill = (src, prefill) =>
  new Promise((resolve, reject) => {
    if (!prefill?.length) return resolve(src)
    AP.context.getContext((context) => {
      if (context.jira?.dashboard) return resolve(src) // is dashboard
      AP.user.getCurrentUser(async (user) => {
        AP.request(`/rest/api/3/issue/${context.jira.issue.key}`)
          .then((res) => JSON.parse(res.body))
          .then((data) => {
            const hostname = new URL(data.self).hostname
            const values = {
              'user.id': user?.atlassianAccountId,
              'issue.key': data.key,
              'issue.summary': data.fields.summary,
              'issue.type': data.fields.issuetype.name,
              'issue.url': `https://${hostname}/browse/${data.key}`,
              'project.key': data.fields.project.key,
              'project.name': data.fields.project.name,
              'project.url': `https://${hostname}/browse/${data.fields.project.key}`,
            }

            // include custom fields
            Object.keys(data.fields).forEach((key) => {
              if (key.startsWith('customfield_')) {
                const value = data.fields[key]
                if (typeof value !== 'object' && value !== null) {
                  values[`custom.${key.split('_')[1]}`] = value
                }
              }
            })

            const url = new URL(src)
            const arr = []
            prefill.forEach(({ field, value }) =>
              arr.push([
                field,
                value.replace(
                  /{{(.*?)}}/g,
                  (match, group1) => values[group1] || ''
                ),
              ])
            )

            const queryParams = new URLSearchParams(arr).toString()
            resolve(
              `${url.protocol}//${url.hostname}${url.pathname}?${queryParams}`
            )
          })
          .catch((error) => {
            console.error('Error fetching issue details:', error)
            reject(error)
          })
      })
    })
  })

export const markersMQL = (query, issue) =>
  new Promise((resolve) => {
    if (!query?.trim()) {
      resolve(null)
      return
    }

    const evaluateCondition = (data, fieldPath, operator, condition) => {
      fieldPath = fieldPath.replace(/^issue\./, '')

      let fieldValue =
        fieldPath === 'id' || fieldPath === 'key'
          ? data[fieldPath]
          : fieldPath
              .split('.')
              .reduce(
                (acc, part) =>
                  acc && acc[part] !== undefined ? acc[part] : undefined,
                data.fields
              )

      if (fieldValue === undefined) return false
      fieldValue = fieldValue.toString()

      switch (operator) {
        case '=':
          return fieldValue.toLowerCase() === condition.toLowerCase()
        case '!=':
          return fieldValue.toLowerCase() !== condition.toLowerCase()
        case '>':
          return parseFloat(fieldValue) > parseFloat(condition)
        case '<':
          return parseFloat(fieldValue) < parseFloat(condition)
        case '>=':
          return parseFloat(fieldValue) >= parseFloat(condition)
        case '<=':
          return parseFloat(fieldValue) <= parseFloat(condition)
        case '~':
          return fieldValue.toLowerCase().includes(condition.toLowerCase())
        case '!~':
          return !fieldValue.toLowerCase().includes(condition.toLowerCase())
        default:
          return false
      }
    }

    const processConditions = (conditions, data) =>
      conditions.split(/\sOR\s/i).some((orCondition) =>
        orCondition
          .trim()
          .split(/\sAND\s/i)
          .every((andCondition) => {
            const match = andCondition
              .trim()
              .match(
                /([a-zA-Z_][\w.]*\w)\s*(>=|<=|>|<|=|!=|~|!~)\s*("([^"]*)"|'([^']*)'|\S+)/i
              )
            if (!match) return false
            const [
              ,
              fieldPath,
              operator,
              ,
              conditionQuoted,
              conditionSingleQuoted,
            ] = match

            const condition =
              conditionQuoted || conditionSingleQuoted || match[3]
            return evaluateCondition(data, fieldPath, operator, condition)
          })
      )

    query.split('\n').forEach((q) => {
      const [, action, quotedValue, singleWordValue, conditions] =
        q.match(/set\s+(\w+)\s*=\s*(?:"(.*?)"|(\S+))(?:\s*where\s*(.+))?/i) ||
        []
      const value = quotedValue || singleWordValue

      if (!conditions || (action && processConditions(conditions, issue))) {
        switch (action.toLowerCase()) {
          case 'color':
            resolve({
              path: 'M 0,0 C -2,-10 -10,-12 -10,-20 A 10,10 0 1,1 10,-20 C 10,-12 2,-10 0,0 z',
              fillColor: value,
              fillOpacity: 1,
              strokeColor: tinycolor(value).darken().toString(),
              strokeWeight: 1,
              scale: 1.25,
            })
            break
          case 'icon':
            resolve(value)
            break
          default:
            return null
        }
      }
    })

    resolve(null)
  })

export const matchedTheme = (macro, themes) => {
  const matchMacro = Helpers.objectExclude(macro, [
    'uuid',
    'attachmentId',
    'loaded',
    'isNew',
    'theme',
  ])
  const match = themes.filter((theme) =>
    Object.entries(matchMacro).every(
      ([k, v]) =>
        JSON.stringify(theme[k])?.toLowerCase() ===
        JSON.stringify(v)?.toLowerCase()
    )
  )
  return match.length ? match[0].theme : 'custom'
}

export const objectExclude = (obj, exclude) =>
  Object.fromEntries(
    Object.entries(obj).filter(([k, v]) => !exclude?.includes(k))
  )

export const preventTyping = (e) => {
  const ctrl = e.ctrlKey || e.keyCode === 17
  if (
    (e.keyCode === 86 && ctrl) ||
    (e.keyCode === 67 && ctrl) ||
    (e.keyCode === 88 && ctrl)
  ) {
    return true
  }
  return false
}

export const reduceObject = (options, selected) =>
  options?.reduce((obj, item) => {
    obj[item.value] = selected?.some((e) => e.value === item.value)
      ? item.on
      : item.off
    return obj
  }, {})

export const removeUndefined = (obj) =>
  Object.keys(obj).reduce(
    (acc, key) =>
      obj[key] === undefined ? { ...acc } : { ...acc, [key]: obj[key] },
    {}
  )

export const sanitizeUrl = (url) => {
  const SAFE_URL_PATTERN =
    /^(?:(?:https?|mailto|ftp|tel|file|sms):|[^&:/?#]*(?:[/?#]|$))/gi
  const DATA_URL_PATTERN =
    /^data:(?:image\/(?:bmp|gif|jpeg|jpg|png|tiff|webp)|video\/(?:mpeg|mp4|ogg|webm)|audio\/(?:mp3|oga|ogg|opus));base64,[a-z0-9+/]+=*$/i
  url = String(url)
  if (url === 'null' || url.length === 0 || url === 'about:blank')
    return 'about:blank'
  if (url.match(SAFE_URL_PATTERN) || url.match(DATA_URL_PATTERN)) return url
  return `unsafe:${url}`
}

export const sanitizeRegex = (str) =>
  String(str).replace(/([.*+?^=!:${}()|[\]/\\])/g, '\\$1')

export const smartValueFields = () =>
  new Promise((resolve) => {
    AP.context.getContext((context) => {
      if (context?.jira?.dashboard) return resolve(false) // is dashboard
      const isJira = context?.jira?.issue?.id
      const requestUrl = isJira
        ? `/rest/api/3/issue/${context?.jira?.issue?.id}`
        : `/api/v2/${
            context?.confluence?.content?.type === 'page'
              ? 'pages'
              : 'blogposts'
          }/${context?.confluence?.content?.id}`

      AP.request(requestUrl)
        .then((res) => JSON.parse(res.body))
        .then((data) => resolve(data))
        .catch(() => {
          resolve(false)
        })
    })
  })

export const smartValueURL = (url, data) => {
  if (!data || !/{{[^{}]+}}/.test(url)) {
    return url
  }
  const fields = data?.fields || data
  const replacedUrl = url.replace(
    /{{(issue\.|page\.)?(.*?)}}/g,
    (match, prefix, path) => {
      if (path === 'id') return encodeURIComponent(data?.id)
      if (path === 'key') return encodeURIComponent(data?.key)
      if (path === 'title') return encodeURIComponent(data?.title)

      const keys = path.split('.')
      const value = keys.reduce(
        (acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined),
        fields
      )
      return value !== undefined ? encodeURIComponent(value) : match
    }
  )

  return replacedUrl
}

export const truncate = (str, max, { side = 'end', ellipsis = '...' } = {}) => {
  if (str.length > max) {
    switch (side) {
      case 'start':
        return ellipsis + str.slice(-(max - ellipsis.length))
      case 'end':
      default:
        return str.slice(0, max - ellipsis.length).trim() + ellipsis
    }
  }
  return str
}

export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)
    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])
  return debouncedValue
}

// https://usehooks.com/usePrevious
export const usePrevious = (value) => {
  const ref = useRef()
  useEffect(() => {
    ref.current = value
  }, [value])
  return ref.current
}

// https://usehooks.com/useScript
export const useScript = (src, attributes) => {
  const [status, setStatus] = useState(src ? 'loading' : 'idle')
  useEffect(() => {
    if (!src) {
      setStatus('idle')
      return
    }
    let script = document.querySelector(`script[src="${src}"]`)
    if (!script) {
      script = document.createElement('script')
      script.src = src
      script.async = true
      if (attributes) {
        attributes.forEach((x) => script.setAttribute(x.key, x.value))
      }
      script.setAttribute('data-status', 'loading')
      document.body.appendChild(script)
      const setAttributeFromEvent = (event) => {
        script.setAttribute(
          'data-status',
          event.type === 'load' ? 'ready' : 'error'
        )
      }
      script.addEventListener('load', setAttributeFromEvent)
      script.addEventListener('error', setAttributeFromEvent)
    } else {
      setStatus(script.getAttribute('data-status'))
    }
    const setStateFromEvent = (event) => {
      setStatus(event.type === 'load' ? 'ready' : 'error')
    }
    script.addEventListener('load', setStateFromEvent)
    script.addEventListener('error', setStateFromEvent)
    return () => {
      if (script) {
        script.removeEventListener('load', setStateFromEvent)
        script.removeEventListener('error', setStateFromEvent)
      }
    }
  }, [src])
  return status
}
